/**
 * Design slice (SCRUM-1952, RTK bundle 3) — the former switch reducer
 * designReducer.js as createSlice. Behaviour-preserving: every case answers the
 * same legacy action type string (designTypes.js) that designAction.js already
 * dispatches, and returns the same state — GET_DESIGNS still reverses the
 * fetched list in place, newest first. The actions module is unchanged, so no
 * importer changes. `CLEAR_MESSAGE` is the same string blog and ad answer to;
 * each slice clears its own message, as the switch reducers did.
 * Characterised by tests/integration/reducers/contentReducers.characterisation.test.js.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  POST_DESIGN,
  GET_DESIGNS,
  DELETE_DESIGN,
  UPDATE_DESIGN,
  CLEAR_MESSAGE,
} from "./designTypes";

// SCRUM-1225: addDesign no longer seeds `settings_variables: {}`. Design-level
// settings are removed; a design carries only name and source.
type DesignState = {
  designs: any[];
  addDesign: { name: string; source: string };
  msg: string;
};

const initialState: DesignState = {
  designs: [],
  addDesign: {
    name: "",
    source: "",
  },
  msg: "",
};

const designSlice = createSlice({
  name: "designs",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(CLEAR_MESSAGE, (state) => ({ ...state, msg: "" }))
      .addCase(POST_DESIGN, (state, action: PayloadAction<{ data: DesignState["addDesign"]; msg: string }, typeof POST_DESIGN>) => ({
        ...state,
        addDesign: action.payload.data,
        msg: action.payload.msg,
      }))
      .addCase(GET_DESIGNS, (state, action: PayloadAction<DesignState["designs"], typeof GET_DESIGNS>) => ({
        ...state,
        designs: action.payload.reverse(),
      }))
      .addCase(DELETE_DESIGN, (state, action: PayloadAction<string, typeof DELETE_DESIGN>) => ({
        ...state,
        designs: state.designs.filter((design) => design._id !== action.payload),
      }))
      .addCase(UPDATE_DESIGN, (state, action: PayloadAction<DesignState["designs"][number], typeof UPDATE_DESIGN>) => ({
        ...state,
        designs: [action.payload, ...state.designs.filter((design) => design._id !== action.payload._id)],
      }));
  },
});

export default designSlice.reducer;
